// B5b: small shared read helpers for the four Materials screens — pull typed
// values off the raw snake_case record `fields` the media/resume routes hand
// back, and map medium/result codes to the human labels the demo prints.
import type { ActorMedia, ResumeEntry } from '../lib/api';

export function mstr(fields: Record<string, unknown>, key: string): string | null {
  const v = fields[key];
  return typeof v === 'string' && v ? v : null;
}

export function mbool(fields: Record<string, unknown>, key: string): boolean {
  return fields[key] === true;
}

/** A look/reel's display label — its own `label`, else a capitalised `kind`. */
export function mediaLabel(m: ActorMedia): string {
  return mstr(m.fields, 'label') ?? cap(String(m.fields['kind'] ?? 'media'));
}

export function cap(s: string): string {
  return s.length ? s[0]!.toUpperCase() + s.slice(1) : s;
}

// Resume: the third credit column (a medium, humanised — we don't resolve the
// production company here; the medium is the honest, real datum we hold).
const MEDIUM_LABEL: Record<string, string> = {
  film: 'Film',
  tv: 'Television',
  theatre: 'Theatre',
  commercial: 'Commercial',
  new_media: 'New Media',
  other: '',
};

export function mediumLabel(medium: string | null): string {
  if (!medium) return '';
  return MEDIUM_LABEL[medium] ?? cap(medium);
}

// Awards: a resume's "2× CSA Nominee" style line — group award records by
// org + result and count them, so three CSA nominations read as one line.
export interface AwardLine {
  key: string;
  text: string;
}

const RESULT_WORD: Record<string, string> = { nominee: 'Nominee', winner: 'Winner', other: 'Award' };

export function summariseAwards(awards: ResumeEntry[]): AwardLine[] {
  const buckets = new Map<string, { count: number; org: string | null; word: string; year: string | null }>();
  for (const a of awards) {
    const org = mstr(a.fields, 'org');
    const result = mstr(a.fields, 'result') ?? 'other';
    const word = RESULT_WORD[result] ?? 'Award';
    const key = `${org ?? ''}|${word}`;
    const prior = buckets.get(key);
    if (prior) prior.count += 1;
    else buckets.set(key, { count: 1, org, word, year: mstr(a.fields, 'year') });
  }
  return [...buckets.entries()].map(([key, b]) => ({
    key,
    text: `${b.count > 1 ? `${b.count}× ` : ''}${b.org ? `${b.org} ` : ''}${b.word}`,
  }));
}
